import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { get } from '../utils/apiInterceptor';
import { MARKETING_URL } from '../utils/adminSite';
import { TERMS_URL, DPA_URL } from '../data/terms';
import { PASSWORD_HINT } from '../utils/passwords';
import './booking/bookingVars.css';
import './AuthPages.css';

const FIELDS = [
  { name: 'firstName', label: 'Your first name', autoComplete: 'given-name' },
  { name: 'lastName', label: 'Your last name', autoComplete: 'family-name' },
  { name: 'email', label: 'Email', type: 'email', autoComplete: 'email' },
  { name: 'password', label: 'Password', type: 'password', autoComplete: 'new-password', hint: PASSWORD_HINT },
  { name: 'clubName', label: 'Club name', autoComplete: 'organization' },
];

// A club signs itself up: the club and its admin in one step
export default function StartClub() {
  const { registerClub, user } = useAuth();
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [open, setOpen] = useState(null); // null while we ask
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', clubName: '', website: '' });
  const [agree, setAgree] = useState(false);
  const [agreeError, setAgreeError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    get('/api/club-signup/status')
      .then((r) => { if (live) setOpen(r.data?.open === true); })
      .catch(() => { if (live) setOpen(false); });
    return () => { live = false; };
  }, []);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setAgreeError('Please tick the box to agree.'); return; }
    setAgreeError('');
    setBusy(true);
    setError('');
    const result = await registerClub({ ...form, agree: true });
    setBusy(false);
    if (result.success) navigate('/dashboard', { replace: true });
    else setError(result.error || 'Sign-up failed. Please try again.');
  };

  if (user) {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">Run The Club</div>
          <h1 className="auth-heading">Start your club</h1>
          <p className="auth-hint">You're already signed in as {name}.</p>
          <div className="auth-links"><Link to="/dashboard" className="auth-link">Go to your dashboard</Link></div>
        </div>
      </div>
    );
  }

  if (open === null) {
    return <div className="auth-page"><div className="auth-card"><div className="auth-brand">Run The Club</div></div></div>;
  }

  if (!open) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">Run The Club</div>
          <h1 className="auth-heading">Start your club</h1>
          <p className="auth-hint">We're not taking new clubs through this page yet.</p>
          <div className="auth-links">
            <a href={`${MARKETING_URL}/#interest`} className="auth-link">Get in touch and we'll let you know</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">Run The Club</div>
        <h1 className="auth-heading">Start your club</h1>
        <p className="auth-hint">Create your club and your admin account. You can add everything else once you're in.</p>
        {error && <p className="auth-error">{error}</p>}
        <form onSubmit={submit} className="auth-form">
          <label style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
            Website
            <input type="text" name="website" tabIndex="-1" autoComplete="off" value={form.website} onChange={change} />
          </label>
          {FIELDS.map((f) => (
            <label className="auth-label" key={f.name}>{f.label}
              <input name={f.name} type={f.type || 'text'} value={form[f.name]} onChange={change}
                className="auth-input" required autoComplete={f.autoComplete}
                minLength={f.name === 'password' ? 8 : undefined} />
              {f.hint && <span className="auth-hint" style={{ margin: 0 }}>{f.hint}</span>}
            </label>
          ))}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1.4 }}>
            <input type="checkbox" checked={agree} className="auth-checkbox"
              onChange={(e) => { setAgree(e.target.checked); if (e.target.checked) setAgreeError(''); }} />
            <span>
              I agree to the{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="auth-link">Terms for clubs</a>
              {' '}and the{' '}
              <a href={DPA_URL} target="_blank" rel="noopener noreferrer" className="auth-link">Data processing agreement</a>
              {' '}on behalf of the club
            </span>
          </label>
          {agreeError && <span className="auth-field-error">{agreeError}</span>}
          <button type="submit" disabled={busy} className="auth-btn auth-btn--primary">
            {busy ? 'Creating club…' : 'Create club'}
          </button>
        </form>
        <div className="auth-links" style={{ flexDirection: 'column', gap: '0.2rem' }}>
          <span>Already have an account?</span>
          <Link to="/login" className="auth-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
